import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons } from './icons';
import type { QualityCheck } from '../types';
import { colors, radius, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

interface QualityCheckCardProps {
  data: QualityCheck;
  testID?: string;
}

function ListBlock({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <View style={styles.block}>
      <Text style={styles.blockTitle}>{title}</Text>
      {items.map((it, i) => (
        <Text key={i} style={styles.item}>
          - {it}
        </Text>
      ))}
    </View>
  );
}

// « Ce qui a été amélioré » : bloc pédagogique repliable, non bloquant, affiché après le CV généré
// quand l'IA a renvoyé un quality_check. Replié par défaut.
export function QualityCheckCard({ data, testID = 'cvc-quality' }: QualityCheckCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <View style={styles.card} testID={testID}>
      <Pressable
        style={styles.header}
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        testID={`${testID}-toggle`}
      >
        <Text style={styles.headerTitle}>{t('qualityCheck.title')}</Text>
        <Ionicons name={open ? 'chevron-up' : 'chevron-down'} size={18} color={colors.grey500} />
      </Pressable>

      {open ? (
        <View style={styles.body} testID={`${testID}-body`}>
          {data.erreurs_corrigees.length > 0 ? (
            <View style={styles.block}>
              <Text style={styles.blockTitle}>{t('qualityCheck.corrections')}</Text>
              {data.erreurs_corrigees.map((e, i) => (
                <View key={i} style={styles.correction}>
                  <Text style={styles.correctionTitle}>{e.erreur}</Text>
                  {e.correction ? <Text style={styles.item}>{e.correction}</Text> : null}
                  {e.explication ? <Text style={styles.why}>{e.explication}</Text> : null}
                </View>
              ))}
            </View>
          ) : null}

          <ListBlock title={t('qualityCheck.languagesRemoved')} items={data.langues_retirees} />
          <ListBlock title={t('qualityCheck.skillsNotProven')} items={data.competences_non_demontrees} />
          <ListBlock title={t('qualityCheck.toValidate')} items={data.a_valider} />
          <ListBlock title={t('qualityCheck.removed')} items={data.elements_supprimes} />
          <ListBlock title={t('qualityCheck.recommendations')} items={data.recommandations} />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: radius.lg,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey200,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.lg,
  },
  headerTitle: { fontSize: typography.bodyMd.size, fontWeight: typography.bodyMd.weight, color: colors.grey900 },
  body: { paddingHorizontal: spacing.lg, paddingBottom: spacing.lg, gap: spacing.md },
  block: { gap: spacing.xs },
  blockTitle: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
  },
  correction: { gap: 2, marginBottom: spacing.xs },
  correctionTitle: { fontSize: typography.small.size, fontWeight: typography.bodyMd.weight, color: colors.grey900 },
  item: { fontSize: typography.small.size, color: colors.grey700 },
  why: { fontSize: typography.small.size, color: colors.grey500, fontStyle: 'italic' },
});
